<template>
  <validation-observer ref="formObserver">
    <b-form @submit.prevent="onSave" class="mt-1">
      <b-row>
        <!-- Left Column -->
        <b-col cols="12" md="6">

          <!-- General Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">{{ $t("dmp.remoteOptions.general") }}</h6>

            <b-form-group :label="$t('dmp.remoteOptions.remoteKey')" label-for="remoteKey">
              <b-input-group class="input-group-merge">
                <b-form-input id="remoteKey" v-model="remoteOptions.remoteKey" :type="remoteKeyFieldType" />
                <b-input-group-append is-text>
                  <feather-icon class="cursor-pointer" :icon="remoteKeyToggleIcon" @click="toggleRemoteKeyVisibility" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>

            <b-form-checkbox v-model="remoteOptions.remoteDisarm" class="mb-1">
              {{ $t("dmp.remoteOptions.remoteDisarm") }}
            </b-form-checkbox>
          </b-card>

          <!-- Dialer Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">{{ $t("dmp.remoteOptions.dialer") }}</h6>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.armedAnswerRings')"
              :rules="{ required: true, min_value: 0, max_value: 15 }">
              <b-form-group :label="$t('dmp.remoteOptions.armedAnswerRings')" label-for="armedAnswerRings">
                <b-form-input id="armedAnswerRings" v-model.number="remoteOptions.armedAnswerRings" type="number"
                  min="0" max="15" placeholder="00" :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.disarmedAnswerRings')"
              :rules="{ required: true, min_value: 0, max_value: 15 }">
              <b-form-group :label="$t('dmp.remoteOptions.disarmedAnswerRings')" label-for="disarmedAnswerRings">
                <b-form-input id="disarmedAnswerRings" v-model.number="remoteOptions.disarmedAnswerRings" type="number"
                  min="0" max="15" placeholder="00" :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-checkbox v-model="remoteOptions.pcModem" class="mb-1">
              {{ $t("dmp.remoteOptions.pcModem") }}
            </b-form-checkbox>

            <b-form-group :label="$t('dmp.remoteOptions.alarmReceiver')" label-for="alarmReceiver">
              <b-form-select id="alarmReceiver" v-model="remoteOptions.alarmReceiver" :options="yesNoOptions" />
            </b-form-group>

            <b-form-group :label="$t('dmp.remoteOptions.serviceReceiver')" label-for="serviceReceiver">
              <b-form-select id="serviceReceiver" v-model="remoteOptions.serviceReceiver" :options="yesNoOptions" />
            </b-form-group>
          </b-card>

          <!-- Network Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">{{ $t("dmp.remoteOptions.network") }}</h6>

            <b-form-checkbox v-model="remoteOptions.allowNetworkRemote" class="mb-1">
              {{ $t("dmp.remoteOptions.allowNetworkRemote") }}
            </b-form-checkbox>

            <b-form-checkbox v-model="remoteOptions.encryptNetworkRemote" class="mb-1">
              {{ $t("dmp.remoteOptions.encryptNetworkRemote") }}
            </b-form-checkbox>
            <small class="text-warning d-flex align-items-center mb-1">
              <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
              <span>{{ $t("dmp.remoteOptions.encryptNetworkRemoteWarning") }}</span>
            </small>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.networkProgrammingPort')"
              :rules="{ required: true, min_value: 0, max_value: 99999 }">
              <b-form-group :label="$t('dmp.remoteOptions.networkProgrammingPort')" label-for="networkProgrammingPort">
                <b-form-input id="networkProgrammingPort" v-model.number="remoteOptions.networkProgrammingPort"
                  type="number" min="0" max="99999" placeholder="00000"
                  :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-card>

          <!-- Cellular Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">{{ $t("dmp.remoteOptions.cellular") }}</h6>

            <b-form-checkbox v-model="remoteOptions.allowCellularRemote" class="mb-1">
              {{ $t("dmp.remoteOptions.allowCellularRemote") }}
            </b-form-checkbox>

            <b-form-checkbox v-model="remoteOptions.encryptCellRemote" class="mb-1"
              :disabled="!remoteOptions.allowCellularRemote">
              {{ $t("dmp.remoteOptions.encryptCellRemote") }}
            </b-form-checkbox>
            <small class="text-warning d-flex align-items-center mb-1">
              <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
              <span>{{ $t("dmp.remoteOptions.encryptCellRemoteWarning") }}</span>
            </small>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.firstGprsApn')"
              :rules="{ max: 32 }">
              <b-form-group :label="$t('dmp.remoteOptions.firstGprsApn')" label-for="cellApn1">
                <b-form-input id="cellApn1" v-model="remoteOptions.cellApn1" type="text" placeholder="SECURECOM522"
                  maxlength="32" :disabled="!remoteOptions.allowCellularRemote"
                  :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.secondGprsApn')"
              :rules="{ max: 32 }">
              <b-form-group :label="$t('dmp.remoteOptions.secondGprsApn')" label-for="cellApn2">
                <b-form-input id="cellApn2" v-model="remoteOptions.cellApn2" type="text" placeholder="SECURECOM523"
                  maxlength="32" :disabled="!remoteOptions.allowCellularRemote"
                  :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-card>
        </b-col>

        <!-- Right Column -->
        <b-col cols="12" md="6">
          <!-- Mobile Application Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">
              {{ $t("dmp.remoteOptions.mobileApplication") }}
            </h6>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.appKey')"
              :rules="{ max: 8 }">
              <b-form-group :label="$t('dmp.remoteOptions.appKey')" label-for="appKey" class="input-group-merge">
                <b-input-group class="input-group-merge">
                  <b-form-input id="appKey" v-model="remoteOptions.appKey" :type="appKeyFieldType" placeholder=""
                    maxlength="8" :state="getValidationState(validationContext)" />
                  <b-input-group-append is-text>
                    <feather-icon class="cursor-pointer" :icon="appKeyToggleIcon" @click="toggleAppKeyVisibility" />
                  </b-input-group-append>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-input-group>
              </b-form-group>
            </validation-provider>
          </b-card>

          <!-- Program Sync Section -->
          <b-card class="mb-1 border">
            <h6 class="mb-2 font-weight-bold">
              {{ $t("dmp.remoteOptions.programSync") }}
            </h6>

            <b-form-group :label="$t('dmp.remoteOptions.sendLocalChanges')" label-for="sendLocalChanges">
              <b-form-select id="sendLocalChanges" v-model="remoteOptions.sendLocalChanges"
                :options="sendLocalChangesOptions" />
            </b-form-group>

            <b-form-group :label="$t('dmp.remoteOptions.remoteChangeIp')" label-for="remoteChangeIp">
              <ip-address-input :value="remoteOptions.remoteChangeIp"
                @input="(val) => (remoteOptions.remoteChangeIp = val)" :disabled="!remoteChangeIpEnabled" />
            </b-form-group>

            <validation-provider #default="validationContext" :name="$t('dmp.remoteOptions.remoteChangePort')" :rules="{
              required: remoteChangePortEnabled,
              min_value: 0,
              max_value: 65535,
            }">
              <b-form-group :label="$t('dmp.remoteOptions.remoteChangePort')" label-for="remoteChangePort">
                <b-form-input id="remoteChangePort" v-model.number="remoteOptions.remoteChangePort" type="number"
                  min="0" max="65535" placeholder="2002" :disabled="!remoteChangePortEnabled"
                  :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group :label="$t('dmp.remoteOptions.remotePhoneNumber')" label-for="remotePhoneNumber">
              <b-form-input id="remotePhoneNumber" v-model="remoteOptions.remotePhoneNumber" type="text" placeholder=""
                :disabled="!remotePhoneNumberEnabled" />
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<script>
import { ref } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import formValidation from "@core/comp-functions/forms/form-validation";
import IpAddressInput from "@/components/InputIp/IpAddressInput.vue";

export default {
  name: "StandardTab",
  components: {
    ValidationObserver,
    ValidationProvider,
    IpAddressInput,
  },
  props: {
    remoteOptions: {
      type: Object,
      required: true,
    },
    yesNoOptions: {
      type: Array,
      required: true,
    },
    sendLocalChangesOptions: {
      type: Array,
      required: true,
    },
    remoteChangeIpEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    remoteChangePortEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    remotePhoneNumberEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const { getValidationState } = formValidation();
    const formObserver = ref(null);

    const remoteKeyFieldType = ref("password");
    const remoteKeyToggleIcon = ref("EyeIcon");
    const toggleRemoteKeyVisibility = () => {
      remoteKeyFieldType.value =
        remoteKeyFieldType.value === "password" ? "text" : "password";
      remoteKeyToggleIcon.value =
        remoteKeyFieldType.value === "password" ? "EyeIcon" : "EyeOffIcon";
    };

    const appKeyFieldType = ref("password");
    const appKeyToggleIcon = ref("EyeIcon");
    const toggleAppKeyVisibility = () => {
      appKeyFieldType.value =
        appKeyFieldType.value === "password" ? "text" : "password";
      appKeyToggleIcon.value =
        appKeyFieldType.value === "password" ? "EyeIcon" : "EyeOffIcon";
    };

    const onSave = async () => {
      const isValid = await formObserver.value.validate();
      if (!isValid) {
        return;
      }
      emit("save");
    };

    return {
      t,
      formObserver,
      getValidationState,
      onSave,
      remoteKeyFieldType,
      remoteKeyToggleIcon,
      toggleRemoteKeyVisibility,
      appKeyFieldType,
      appKeyToggleIcon,
      toggleAppKeyVisibility
    };
  },
};
</script>

<style scoped lang="scss">
.border {
  border: 1px solid #ebe9f1;
}
</style>
